<template>
  <v-dialog max-width="500" :value="show" @input="closeModal">
    <v-card class="comment-form-modal">
      <v-card-title class="modal-header">
        <h3 class="modal-title mb-3">
          {{ isEditing ? $t('activities.edit_comment') : $t('activities.add_comments') }}
        </h3>
      </v-card-title>

      <v-card-content class="modal-content">
        <form @submit.prevent="submitComment">
          <!-- Author Field (Read Only) -->
          <div class="form-group">
            <v-input-r 
              :label="$t('activities.author')"
              :focus="false" 
              v-model="form.author" 
              :disabled="true" 
            />
          </div>

          <!-- Comment Field -->
          <div class="form-group">
            <v-textarea 
              id="message-comment" 
              :label="$t('activities.comment')"
              :hint="errorMessage('comment')"
              :invalid="!!errorMessage('comment')" 
              v-model="form.comment" 
              :rows="6" 
              :auto-resize="true"
              :max-length="1000" 
              :focus="true"
            />
          </div>
        </form>
      </v-card-content>

      <v-card-actions>
        <v-btn 
          :disabled="!isValid" 
          :loading="loading" 
          @click="submitComment" 
          class="submit-btn"
        >
          {{ isEditing ? $t('activities.update_comment') : $t('activities.add_comments') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import formErrorsMixin from '@/mixins/form-errors.mixin.js'
import api from '@/api'

export default {
  name: 'CommentFormModal',

  mixins: [formErrorsMixin],

  props: {
    show: {
      type: Boolean,
      default: false
    },
    client: {
      type: Object,
      default: null
    },
    farm: {
      type: Object,
      default: null
    },
    defaultAuthor: {
      type: String,
      default: 'Директор'
    },
    existingComment: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      form: {
        author: '',
        comment: ''
      },
      errors: {
        comment: null
      },
      loading: false
    }
  },

  computed: {
    isValid () {
      return this.form.comment &&
             this.form.comment.trim().length >= 10 &&
             !this.errors.comment
    },

    isEditing () {
      return Boolean(this.existingComment)
    }
  },

  watch: {
    show (newVal) {
      if (newVal) {
        this.initializeForm()
      }
    },

    existingComment: {
      handler () {
        if (this.show) {
          this.initializeForm()
        }
      },
      deep: true
    }
  },

  mounted () {
    document.addEventListener('keydown', this.handleEscapeKey)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this.handleEscapeKey)
  },

  methods: {
    initializeForm () {
      this.form = {
        author: this.defaultAuthor,
        comment: this.existingComment ? this.existingComment.description : ''
      }
      this.clearErrors()
    },

    closeModal () {
      this.resetForm()
      this.$emit('close')
    },

    resetForm () {
      this.form = {
        author: '',
        comment: ''
      }
      this.clearErrors()
    },

    clearErrors () {
      this.errors = {
        comment: null
      }
    },

    validateForm () {
      this.clearErrors()

      if (!this.form.comment || !this.form.comment.trim()) {
        this.errors.comment = ['3'] // required field
      } else if (this.form.comment.trim().length < 10) {
        this.errors.comment = ['5', 10] // minimum length
      } else if (this.form.comment.trim().length > 1000) {
        this.errors.comment = ['6', 1000] // maximum length
      }

      return !this.errors.comment
    },
    async submitComment () {
      if (!this.client.id) {
        console.error('No client specified for comment')
        return
      }

      this.loading = true

      if (!this.validateForm()) {
        this.loading = false
        return
      }

      try {
        const payload = {
          description: this.form.comment.trim()
        }

        if (this.farm) {
          payload.farm = this.farm.id
        }

        const res = await api.dashboard.groups.createClientComment(this.client.id, payload)

        this.$emit('create', {
          clientId: this.client.id,
          farmId: this.farm ? this.farm.id : null,
          result: res.data
        })
        this.closeModal()
      } catch (error) {
        console.error('Failed to save comment:', error)
        if (error.response && error.response.data && error.response.data.description) {
          this.errors.comment = [error.response.data.description]
        } else {
          const action = this.isEditing ? 'оновленні' : 'створенні'
          this.errors.comment = [`Помилка при ${action} коментаря. Спробуйте пізніше.`]
        }
      } finally {
        this.loading = false
      }
    },

    handleEscapeKey (event) {
      if (event.key === 'Escape' && this.show) {
        this.closeModal()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card__content {
  padding: 2rem;
}

.comment-form-modal {
  .modal-header {
    padding: 2rem 2.4rem 1.6rem;
    border-bottom: 0.1rem solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .modal-title {
      font-size: 1.8rem;
      font-weight: 600;
      color: #333;
      margin: 0;
    }
  }

  .modal-content {
    padding: 2.4rem 2.4rem 0;
  }
}

.form-group {
  margin-bottom: 3rem;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.v-card__title {
  border-bottom: 0.1rem solid #d7d7d7;
  margin-bottom: 2rem;
}

.v-card__actions {
  padding: 0 2.4rem 2.4rem;

  .submit-btn {
    width: 100%;
    background: #4CAF50;
    color: white;
    border: none;
    padding: 1.4rem 2.4rem;
    border-radius: 0.8rem;
    font-size: 1.5rem;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #45a049;
      box-shadow: 0 0.2rem 0.8rem rgba(76, 175, 80, 0.3);
    }

    &:disabled {
      background: #cccccc;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
}

@media (max-width: 48rem) {
  .comment-form-modal {
    .modal-header {
      padding: 1.6rem 2rem 1.2rem;

      .modal-title {
        font-size: 1.6rem;
      }
    }

    .modal-content {
      padding: 2rem 2rem 0;
    }
  }

  .v-card__actions {
    padding: 0 2rem 2rem;

    .submit-btn {
      padding: 1.2rem 2rem;
      font-size: 1.4rem;
    }
  }
}
</style>